// src/components/satellite/GoogleMapsSatelliteUpload.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, Camera, AlertCircle, Check, Edit3, Loader } from 'lucide-react';
import { doc, updateDoc, serverTimestamp, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../utils/firebase';

// ============================================================================
// HINT COMPONENT - Dismissable yellow hints with arrows (Apple-inspired)
// ============================================================================
const Hint = ({ text, position = 'bottom', onDismiss, style = {} }) => {
  const getArrowStyle = () => {
    const baseArrow = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    switch (position) {
      case 'top':
        return {
          ...baseArrow,
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '8px 8px 0 8px',
          borderColor: '#fef3c7 transparent transparent transparent'
        };
      case 'bottom':
        return {
          ...baseArrow,
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '0 8px 8px 8px',
          borderColor: 'transparent transparent #fef3c7 transparent'
        };
      case 'left':
        return {
          ...baseArrow,
          right: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: '8px 8px 8px 0',
          borderColor: 'transparent #fef3c7 transparent transparent'
        };
      case 'right':
        return {
          ...baseArrow,
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: '8px 0 8px 8px',
          borderColor: 'transparent transparent transparent #fef3c7'
        };
      default:
        return baseArrow;
    }
  };

  return (
    <div style={{
      position: 'relative',
      display: 'inline-block',
      ...style
    }}>
      <div style={getArrowStyle()} />
      <div style={{
        backgroundColor: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: '8px',
        padding: '12px 36px 12px 12px',
        fontSize: '13px',
        color: '#92400e',
        fontWeight: '500',
        lineHeight: '1.4',
        maxWidth: '320px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        position: 'relative'
      }}>
        {text}
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'transparent',
              border: 'none',
              color: '#92400e',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              padding: '0',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fde68a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Dismiss hint"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

// Loading Transition Component
const LoadingTransition = ({ message = 'Loading...' }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10005
    }}>
      <div style={{
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }}>
        <Loader size={48} color="#3b82f6" />
      </div>
      <p style={{
        color: '#1f2937',
        fontSize: '16px',
        fontWeight: '500',
        textAlign: 'center'
      }}>
        {message}
      </p>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const GoogleMapsSatelliteUpload = ({ clubId, clubName, facilityId, onCaptureComplete, googleMapsApiKey, existingConfig }) => {
  const [user] = useAuthState(auth);
  const [map, setMap] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Manual draw states
  const [showManualDraw, setShowManualDraw] = useState(false);
  const [capturedImageBlob, setCapturedImageBlob] = useState(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState(null);
  const [manualPitches, setManualPitches] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [pendingPitch, setPendingPitch] = useState(null);
  const [rotationAdjustment, setRotationAdjustment] = useState(0);
  
  // NEW: Track if save was successful (to prevent UI reset)
  const [saveCompleted, setSaveCompleted] = useState(false);
  
  // Edit mode - when existing config is provided
  const [isEditMode, setIsEditMode] = useState(false);
  
  // CORNER EDITING: Track selected pitch and corner dragging
  const [selectedPitchIndex, setSelectedPitchIndex] = useState(null);
  const [draggingCorner, setDraggingCorner] = useState(null); // { pitchIndex, cornerIndex } or null
  
  // Hint states
  const [showSearchHint, setShowSearchHint] = useState(true);
  const [showCaptureHint, setShowCaptureHint] = useState(true);
  
  // Overlap warning modal state
  const [overlapWarning, setOverlapWarning] = useState(null); // { overlappingNames: [], pitchToAdd: {} }

  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Ref to always have current manualPitches (avoids stale closure in async img.onload)
  const manualPitchesRef = useRef(manualPitches);
  manualPitchesRef.current = manualPitches;
  
  // Ref for selectedPitchIndex and draggingCorner (for async canvas redraw)
  const selectedPitchIndexRef = useRef(selectedPitchIndex);
  selectedPitchIndexRef.current = selectedPitchIndex;
  const draggingCornerRef = useRef(draggingCorner);
  draggingCornerRef.current = draggingCorner;
  
  // ============================================================================
  // POLYGON OVERLAP DETECTION - Prevents users from drawing overlapping pitches
  // ============================================================================
  
  // Check if two line segments intersect
  const doSegmentsIntersect = (p1, p2, p3, p4) => {
    const ccw = (A, B, C) => (C.y - A.y) * (B.x - A.x) > (B.y - A.y) * (C.x - A.x);
    return ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);
  };
  
  // Check if two polygons overlap (either contains points or edges intersect)
  const doPolygonsOverlap = (poly1, poly2) => {
    if (!poly1 || !poly2 || poly1.length < 3 || poly2.length < 3) return false;
    
    // Check if any vertex of poly1 is inside poly2
    for (const point of poly1) {
      if (isPointInPolygon(point, poly2)) return true;
    }
    
    // Check if any vertex of poly2 is inside poly1
    for (const point of poly2) {
      if (isPointInPolygon(point, poly1)) return true;
    }
    
    // Check if any edges intersect
    for (let i = 0; i < poly1.length; i++) {
      const p1 = poly1[i];
      const p2 = poly1[(i + 1) % poly1.length];
      
      for (let j = 0; j < poly2.length; j++) {
        const p3 = poly2[j];
        const p4 = poly2[(j + 1) % poly2.length];
        
        if (doSegmentsIntersect(p1, p2, p3, p4)) return true;
      }
    }
    
    return false;
  };
  
  // Check a new pitch against all existing pitches for overlap
  const checkOverlapWithExisting = (newPitchCorners) => {
    const overlapping = [];
    manualPitches.forEach(pitch => {
      if (pitch.corners && doPolygonsOverlap(newPitchCorners, pitch.corners)) {
        overlapping.push(pitch.name || `Pitch ${pitch.pitchNumber || '?'}`);
      }
    });
    return overlapping;
  };
  
  // Apply rotation adjustment to corners and return the final corners
  const getAdjustedCorners = (pitch, rotationDeg) => {
    if (!pitch.corners || pitch.corners.length !== 4) return null;
    
    const adjustedRotation = rotationDeg * Math.PI / 180;
    if (adjustedRotation === 0) return pitch.corners;
    
    return pitch.corners.map(corner => {
      const dx = corner.x - pitch.centerX;
      const dy = corner.y - pitch.centerY;
      const cos = Math.cos(adjustedRotation);
      const sin = Math.sin(adjustedRotation);
      return {
        x: pitch.centerX + (dx * cos - dy * sin),
        y: pitch.centerY + (dx * sin + dy * cos)
      };
    });
  };

  // Debug: Log component mount
  useEffect(() => {
    console.log('🔄 GoogleMapsSatelliteUpload MOUNTED - fresh component');
    console.log('📊 Initial manualPitches:', manualPitches);
    return () => {
      console.log('🗑️ GoogleMapsSatelliteUpload UNMOUNTING');
    };
  }, []);

  // Load existing data when provided (edit mode)
  useEffect(() => {
    console.log('📊 Checking existing config:', existingConfig);
    
    // Get pitches from either pitches or pitchBoundaries field
    const existingPitchData = existingConfig?.pitches || existingConfig?.pitchBoundaries || [];
    
    if (existingConfig?.imageUrl && existingPitchData.length > 0) {
      // Don't overwrite if we already have pitches loaded (prevents race condition)
      if (manualPitches.length > 0) {
        console.log('📊 Pitches already loaded, skipping reload');
        return;
      }
      
      console.log('📥 Loading existing config for editing');
      setIsEditMode(true);
      setCapturedImageUrl(existingConfig.imageUrl);
      setShowManualDraw(true);
      
      // Load existing pitches - ensure we have the right format
      const loadedPitches = existingPitchData.map(pitch => ({
        id: pitch.id,
        name: pitch.name,
        pitchNumber: pitch.pitchNumber,
        centerX: pitch.centerX,
        centerY: pitch.centerY,
        width: pitch.width,
        height: pitch.height,
        rotation: pitch.rotation || 0,
        corners: pitch.corners || null
      }));
      setManualPitches(loadedPitches);
      console.log('📊 Loaded existing pitches:', loadedPitches.length, loadedPitches);
      
      // Fetch the image as blob for saving later
      fetch(existingConfig.imageUrl)
        .then(res => res.blob())
        .then(blob => {
          setCapturedImageBlob(blob);
          console.log('📦 Loaded existing image blob');
        })
        .catch(err => console.warn('Could not fetch existing image blob:', err));
    }
  }, [existingConfig, manualPitches.length]);

  const handleDismissSearchHint = () => {
    setShowSearchHint(false);
  };

  const handleDismissCaptureHint = () => {
    setShowCaptureHint(false);
  };

  // ============================================================================
  // MANUAL DRAWING FUNCTIONS
  // ============================================================================

  // CORNER EDITING: Check if a point is inside a polygon
  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // CORNER EDITING: Find if click is near a corner of the selected pitch
  const findNearbyCorner = (pos, pitchIndex, threshold = 40) => {
    if (pitchIndex === null || pitchIndex === undefined) return null;
    
    const pitch = manualPitches[pitchIndex];
    if (!pitch?.corners || pitch.corners.length !== 4) return null;

    for (let i = 0; i < 4; i++) {
      const corner = pitch.corners[i];
      const distance = Math.sqrt(Math.pow(pos.x - corner.x, 2) + Math.pow(pos.y - corner.y, 2));
      
      if (distance <= threshold) {
        return { pitchIndex, cornerIndex: i };
      }
    }
    return null;
  };

  // CORNER EDITING: Recalculate pitch properties from corners
  const recalculatePitchFromCorners = (corners, existingPitch) => {
    const centerX = corners.reduce((sum, p) => sum + p.x, 0) / 4;
    const centerY = corners.reduce((sum, p) => sum + p.y, 0) / 4;

    const edge1 = Math.sqrt(
      Math.pow(corners[1].x - corners[0].x, 2) + 
      Math.pow(corners[1].y - corners[0].y, 2)
    );
    const edge2 = Math.sqrt(
      Math.pow(corners[2].x - corners[1].x, 2) + 
      Math.pow(corners[2].y - corners[1].y, 2)
    );
    
    const rotation = Math.atan2(
      corners[1].y - corners[0].y,
      corners[1].x - corners[0].x
    );

    return {
      ...existingPitch,
      centerX,
      centerY,
      width: edge1,
      height: edge2,
      rotation,
      corners: corners
    };
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const pos = { x, y };

    // If in drawing mode, handle corner placement
    if (isDrawing) {
      console.log('🖱️ CLICK:', {
        canvasInternalSize: `${canvas.width}x${canvas.height}`,
        canvasDisplaySize: `${rect.width.toFixed(0)}x${rect.height.toFixed(0)}`,
        scale: `${scaleX.toFixed(2)}x${scaleY.toFixed(2)}`,
        rawClick: `${(e.clientX - rect.left).toFixed(0)},${(e.clientY - rect.top).toFixed(0)}`,
        scaledClick: `${x.toFixed(0)},${y.toFixed(0)}`,
        pointNumber: currentPoints.length + 1
      });

      const newPoints = [...currentPoints, { x, y }];
      setCurrentPoints(newPoints);

      if (newPoints.length === 4) {
        const newPitch = convertPolygonToRotatedRect(newPoints);
        console.log('✅ Pitch created:', newPitch);
        setPendingPitch(newPitch);
        setRotationAdjustment(0);
        setIsDrawing(false);
      }
      return;
    }

    // Not in drawing mode - handle pitch selection
    if (manualPitches.length === 0) return;

    // Check if clicking on a pitch
    for (let i = 0; i < manualPitches.length; i++) {
      const pitch = manualPitches[i];
      
      if (pitch.corners && pitch.corners.length === 4) {
        if (isPointInPolygon(pos, pitch.corners)) {
          setSelectedPitchIndex(i === selectedPitchIndex ? null : i);
          console.log('🎯 Selected pitch:', i);
          return;
        }
      } else {
        // Legacy rectangle check
        const dx = pos.x - pitch.centerX;
        const dy = pos.y - pitch.centerY;
        const localX = dx * Math.cos(-pitch.rotation) - dy * Math.sin(-pitch.rotation);
        const localY = dx * Math.sin(-pitch.rotation) + dy * Math.cos(-pitch.rotation);

        if (Math.abs(localX) <= pitch.width / 2 && Math.abs(localY) <= pitch.height / 2) {
          setSelectedPitchIndex(i === selectedPitchIndex ? null : i);
          console.log('🎯 Selected pitch:', i);
          return;
        }
      }
    }
    
    // Clicked outside all pitches - deselect
    if (selectedPitchIndex !== null) {
      setSelectedPitchIndex(null);
    }
  };

  // CORNER EDITING: Handle mouse down for corner dragging
  const handleCanvasMouseDown = (e) => {
    if (isDrawing || pendingPitch) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const pos = { x, y };

    // Check if clicking on a corner of the selected pitch
    if (selectedPitchIndex !== null) {
      const corner = findNearbyCorner(pos, selectedPitchIndex, 40);
      if (corner) {
        console.log('🎯 Started dragging corner:', corner);
        setDraggingCorner(corner);
        e.preventDefault();
        return;
      }
    }
  };

  // CORNER EDITING: Handle mouse move for corner dragging
  const handleCanvasMouseMove = (e) => {
    if (!draggingCorner) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const newX = (e.clientX - rect.left) * scaleX;
    const newY = (e.clientY - rect.top) * scaleY;

    // Update the corner position
    setManualPitches(prev => {
      const updated = [...prev];
      const pitch = updated[draggingCorner.pitchIndex];
      
      if (pitch?.corners) {
        const newCorners = [...pitch.corners];
        newCorners[draggingCorner.cornerIndex] = { x: newX, y: newY };
        updated[draggingCorner.pitchIndex] = recalculatePitchFromCorners(newCorners, pitch);
      }
      
      return updated;
    });
  };

  // CORNER EDITING: Handle mouse up to stop dragging
  const handleCanvasMouseUp = (e) => {
    if (draggingCorner) {
      console.log('🎯 Finished dragging corner');
      setDraggingCorner(null);
    }
  };

  const convertPolygonToRotatedRect = (points) => {
    // Sort points clockwise from centroid to get consistent ordering
    const centerX = points.reduce((sum, p) => sum + p.x, 0) / 4;
    const centerY = points.reduce((sum, p) => sum + p.y, 0) / 4;

    const sortedPoints = [...points].sort((a, b) => {
      const angleA = Math.atan2(a.y - centerY, a.x - centerX);
      const angleB = Math.atan2(b.y - centerY, b.x - centerX);
      return angleA - angleB;
    });

    // Calculate approximate dimensions just for display/metadata
    // But the REAL shape is defined by the corners array
    const edge1 = Math.sqrt(
      Math.pow(sortedPoints[1].x - sortedPoints[0].x, 2) + 
      Math.pow(sortedPoints[1].y - sortedPoints[0].y, 2)
    );
    const edge2 = Math.sqrt(
      Math.pow(sortedPoints[2].x - sortedPoints[1].x, 2) + 
      Math.pow(sortedPoints[2].y - sortedPoints[1].y, 2)
    );
    
    const width = edge1;
    const height = edge2;
    const rotation = Math.atan2(
      sortedPoints[1].y - sortedPoints[0].y,
      sortedPoints[1].x - sortedPoints[0].x
    );

    return {
      centerX,
      centerY,
      width,
      height,
      rotation,
      corners: sortedPoints, // THIS is what actually gets drawn - not the rectangle
      name: `Pitch ${manualPitches.length + 1}`
    };
  };

  const drawManualPitches = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    const img = new Image();
    img.onload = () => {
      // Match the capture resolution
      canvas.width = 2048;
      canvas.height = 1536;
      
      // Enable high-quality image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, 2048, 1536);

      // Use ref to get current pitches (avoids stale closure)
      const currentPitches = manualPitchesRef.current;
      const currentSelectedIndex = selectedPitchIndexRef.current;
      const currentDraggingCorner = draggingCornerRef.current;
      
      // Draw existing pitches
      console.log('🎨 Drawing', currentPitches.length, 'existing pitches');
      currentPitches.forEach((pitch, index) => {
        const isSelected = currentSelectedIndex === index;
        
        // If pitch has corners (new format), draw as polygon
        if (pitch.corners && pitch.corners.length === 4) {
          // Selected pitch gets red highlight, others get green
          ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(34, 197, 94, 0.3)';
          ctx.strokeStyle = isSelected ? '#dc2626' : '#10b981';
          ctx.lineWidth = isSelected ? 4 : 3;
          
          ctx.beginPath();
          ctx.moveTo(pitch.corners[0].x, pitch.corners[0].y);
          for (let i = 1; i < 4; i++) {
            ctx.lineTo(pitch.corners[i].x, pitch.corners[i].y);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          // CORNER EDITING: Draw corner handles on selected pitch
          if (isSelected) {
            pitch.corners.forEach((corner, cornerIdx) => {
              const isBeingDragged = currentDraggingCorner?.pitchIndex === index && currentDraggingCorner?.cornerIndex === cornerIdx;
              
              // White outer circle
              ctx.fillStyle = 'white';
              ctx.beginPath();
              ctx.arc(corner.x, corner.y, isBeingDragged ? 18 : 14, 0, 2 * Math.PI);
              ctx.fill();
              
              // Colored inner circle
              ctx.fillStyle = isBeingDragged ? '#ef4444' : '#3b82f6';
              ctx.strokeStyle = isBeingDragged ? '#b91c1c' : '#1d4ed8';
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.arc(corner.x, corner.y, isBeingDragged ? 14 : 10, 0, 2 * Math.PI);
              ctx.fill();
              ctx.stroke();
            });
          }
        } else {
          // Legacy format: draw as rotated rectangle
          ctx.save();
          ctx.translate(pitch.centerX, pitch.centerY);
          ctx.rotate(pitch.rotation);
          ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(34, 197, 94, 0.3)';
          ctx.strokeStyle = isSelected ? '#dc2626' : '#10b981';
          ctx.lineWidth = isSelected ? 4 : 3;
          ctx.fillRect(-pitch.width/2, -pitch.height/2, pitch.width, pitch.height);
          ctx.strokeRect(-pitch.width/2, -pitch.height/2, pitch.width, pitch.height);
          ctx.restore();
        }

        // Draw pitch label - use actual pitch name, not index
        const label = pitch.name || `P${index + 1}`;
        // Extract just the number for display on map (e.g., "Pitch 7" -> "P7")
        const displayLabel = label.replace('Pitch ', 'P');
        ctx.fillStyle = isSelected ? '#dc2626' : '#10b981';
        ctx.font = isSelected ? 'bold 52px Arial' : 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(displayLabel, pitch.centerX, pitch.centerY);
      });

      // Draw pending pitch with rotation adjustment
      if (pendingPitch) {
        // If using corners (new format), draw as adjustable polygon
        if (pendingPitch.corners && pendingPitch.corners.length === 4) {
          const adjustedRotation = rotationAdjustment * Math.PI / 180;
          
          // Rotate corners around center
          const rotatedCorners = pendingPitch.corners.map(corner => {
            const dx = corner.x - pendingPitch.centerX;
            const dy = corner.y - pendingPitch.centerY;
            const cos = Math.cos(adjustedRotation);
            const sin = Math.sin(adjustedRotation);
            return {
              x: pendingPitch.centerX + (dx * cos - dy * sin),
              y: pendingPitch.centerY + (dx * sin + dy * cos)
            };
          });
          
          ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 3;
          
          ctx.beginPath();
          ctx.moveTo(rotatedCorners[0].x, rotatedCorners[0].y);
          for (let i = 1; i < 4; i++) {
            ctx.lineTo(rotatedCorners[i].x, rotatedCorners[i].y);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        } else {
          // Legacy: rotated rectangle
          const adjustedRotation = pendingPitch.rotation + (rotationAdjustment * Math.PI / 180);
          ctx.save();
          ctx.translate(pendingPitch.centerX, pendingPitch.centerY);
          ctx.rotate(adjustedRotation);
          ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 3;
          ctx.fillRect(-pendingPitch.width/2, -pendingPitch.height/2, pendingPitch.width, pendingPitch.height);
          ctx.strokeRect(-pendingPitch.width/2, -pendingPitch.height/2, pendingPitch.width, pendingPitch.height);
          ctx.restore();
        }

        // Draw "NEW" label
        ctx.fillStyle = '#3b82f6';
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('NEW', pendingPitch.centerX, pendingPitch.centerY);
      }

      // Draw current drawing points
      if (currentPoints.length > 0) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(currentPoints[0].x, currentPoints[0].y);
        currentPoints.forEach(point => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();

        // Draw points - 3x larger markers
        currentPoints.forEach((point, index) => {
          ctx.fillStyle = '#3b82f6';
          ctx.beginPath();
          ctx.arc(point.x, point.y, 18, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'white';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(index + 1, point.x, point.y);
        });
      }
    };
    img.src = capturedImageUrl;
  };

  useEffect(() => {
    if (showManualDraw && capturedImageUrl) {
      drawManualPitches();
    }
  }, [showManualDraw, capturedImageUrl, manualPitches, currentPoints, pendingPitch, rotationAdjustment, selectedPitchIndex, draggingCorner]);

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setCurrentPoints([]);
    setSelectedPitchIndex(null); // Clear selection when starting to draw
    setDraggingCorner(null);
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
    setCurrentPoints([]);
  };

  const handleConfirmPitch = () => {
    if (!pendingPitch) return;
    
    // Get the final corners with rotation applied
    const finalCorners = getAdjustedCorners(pendingPitch, rotationAdjustment);
    
    const finalPitch = {
      ...pendingPitch,
      rotation: pendingPitch.rotation + (rotationAdjustment * Math.PI / 180),
      corners: finalCorners || pendingPitch.corners
    };
    
    // Check for overlaps with existing pitches
    if (finalCorners) {
      const overlappingNames = checkOverlapWithExisting(finalCorners);
      if (overlappingNames.length > 0) {
        // Show warning modal instead of adding immediately
        console.log('⚠️ Overlap detected with:', overlappingNames);
        setOverlapWarning({
          overlappingNames,
          pitchToAdd: finalPitch
        });
        return;
      }
    }
    
    // No overlap - add normally
    setManualPitches([...manualPitches, finalPitch]);
    setPendingPitch(null);
    setRotationAdjustment(0);
    setCurrentPoints([]);
  };
  
  // Handle user choosing to add pitch despite overlap warning
  const handleConfirmOverlappingPitch = () => {
    if (!overlapWarning?.pitchToAdd) return;
    
    console.log('📝 User confirmed adding overlapping pitch');
    setManualPitches([...manualPitches, overlapWarning.pitchToAdd]);
    setPendingPitch(null);
    setRotationAdjustment(0);
    setCurrentPoints([]);
    setOverlapWarning(null);
  };
  
  // Handle user canceling the overlapping pitch
  const handleCancelOverlappingPitch = () => {
    console.log('📝 User canceled overlapping pitch');
    setOverlapWarning(null);
    // Keep pending pitch so they can adjust or cancel
  };

  const handleCancelPitch = () => {
    setPendingPitch(null);
    setRotationAdjustment(0);
    setCurrentPoints([]);
    setIsDrawing(true);
  };

  const handleDeletePitch = (index) => {
    const newPitches = manualPitches.filter((_, i) => i !== index);
    setManualPitches(newPitches);
    // Clear selection if we deleted the selected pitch
    if (selectedPitchIndex === index) {
      setSelectedPitchIndex(null);
    } else if (selectedPitchIndex !== null && selectedPitchIndex > index) {
      // Adjust selection index if we deleted a pitch before the selected one
      setSelectedPitchIndex(selectedPitchIndex - 1);
    }
  };

  // ============================================================================
  // FIXED: handleSaveManualPitches - No longer resets state after save
  // The parent component (FacilitySetupWizard) will detect the save via onSnapshot
  // and show the success state automatically
  // ============================================================================
  const handleSaveManualPitches = async () => {
    try {
      setIsSavingManual(true);
      console.log('Starting manual pitch save process...');
      console.log('🏟️ Facility ID:', facilityId || 'NONE (saving to club)');
      console.log('📝 Edit mode:', isEditMode);
      
      if (!capturedImageBlob && !isEditMode) {
        throw new Error('No captured image found');
      }

      if (manualPitches.length === 0) {
        throw new Error('Please draw at least one pitch boundary before saving');
      }

      let imageUrl;
      
      // In edit mode with existing image, skip re-upload (avoids CORS/tainted canvas issues)
      if (isEditMode && capturedImageUrl && capturedImageUrl.startsWith('https://')) {
        console.log('📷 Edit mode - using existing image URL (skipping re-upload)');
        imageUrl = capturedImageUrl;
      } else {
        // Fresh capture - need to resize and upload
        // CRITICAL: Create a properly sized 2048x1536 image for upload
        // The original capturedImageBlob is 4096x3072 (due to scale=2) but our coordinates are 2048x1536
        // We need to resize the image to match our coordinate system
        const resizedBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const resizeCanvas = document.createElement('canvas');
            resizeCanvas.width = 2048;
            resizeCanvas.height = 1536;
            const ctx = resizeCanvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, 2048, 1536);
            resizeCanvas.toBlob(resolve, 'image/jpeg', 0.92);
          };
          img.onerror = reject;
          img.src = capturedImageUrl;
        });
        console.log('📐 Resized image to 2048x1536 for upload');

        // Use facility-specific path if facilityId exists
        const storagePath = facilityId 
          ? `clubs/${clubId}/facilities/${facilityId}/satellite_image.jpg`
          : `clubs/${clubId}/satellite_image.jpg`;
        console.log('📁 Storage path:', storagePath);
        
        console.log('Uploading resized satellite image to Firebase Storage...');
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, resizedBlob);
        imageUrl = await getDownloadURL(storageRef);
        console.log('Image uploaded successfully:', imageUrl);
      }

      // Get current map center for weather alerts (only if map is available)
      let mapLocation = null;
      if (map) {
        const center = map.getCenter();
        mapLocation = {
          lat: center.lat(),
          lng: center.lng()
        };
        console.log('Saving map location for weather alerts:', mapLocation);
      } else {
        console.log('⚠️ No map reference - skipping location save');
      }

      // Get global max pitch number for NEW facilities (to avoid conflicts)
      // Need to check BOTH club document AND all facilities
      const clubDocRef = doc(db, 'clubs', clubId);
      const clubDoc = await getDoc(clubDocRef);
      let globalMaxPitchNumber = 0;
      
      // Check pitches in club document (legacy/primary facility)
      if (clubDoc.exists()) {
        const clubData = clubDoc.data();
        console.log('📋 Club document keys:', Object.keys(clubData));
        
        // Helper function to extract pitch number from various formats
        const extractPitchNumber = (pitch) => {
          // Try pitchNumber field first
          if (pitch.pitchNumber) {
            return parseInt(pitch.pitchNumber, 10) || 0;
          }
          // Try extracting from id (e.g., "pitch-7" -> 7)
          if (pitch.id) {
            const idMatch = pitch.id.match(/(\d+)/);
            if (idMatch) return parseInt(idMatch[1], 10);
          }
          // Try extracting from name (e.g., "Pitch 7" -> 7)
          if (pitch.name) {
            const nameMatch = pitch.name.match(/(\d+)/);
            if (nameMatch) return parseInt(nameMatch[1], 10);
          }
          return 0;
        };
        
        // Check pitches array at root level
        if (clubData.pitches && Array.isArray(clubData.pitches)) {
          console.log(`  - Found ${clubData.pitches.length} pitches at club.pitches`);
          clubData.pitches.forEach(p => {
            const pNum = extractPitchNumber(p);
            console.log(`    - Pitch: id=${p.id}, pitchNumber=${p.pitchNumber}, name=${p.name}, extracted=${pNum}`);
            if (pNum > globalMaxPitchNumber) {
              globalMaxPitchNumber = pNum;
            }
          });
        }
        
        // Also check satelliteConfig.pitches
        if (clubData.satelliteConfig?.pitches && Array.isArray(clubData.satelliteConfig.pitches)) {
          console.log(`  - Found ${clubData.satelliteConfig.pitches.length} pitches at club.satelliteConfig.pitches`);
          clubData.satelliteConfig.pitches.forEach(p => {
            const pNum = extractPitchNumber(p);
            if (pNum > globalMaxPitchNumber) {
              globalMaxPitchNumber = pNum;
            }
          });
        }
        
        // Also check pitchNames keys for highest number
        if (clubData.pitchNames) {
          const pitchNameKeys = Object.keys(clubData.pitchNames);
          console.log(`  - Found ${pitchNameKeys.length} pitchNames entries`);
          pitchNameKeys.forEach(key => {
            // Extract number from keys like "pitch-7", "7", "Pitch 7"
            const match = key.match(/(\d+)/);
            if (match) {
              const num = parseInt(match[1], 10);
              if (num > globalMaxPitchNumber) {
                globalMaxPitchNumber = num;
              }
            }
          });
        }
        
        console.log('🔢 Max pitch number from club document:', globalMaxPitchNumber);
      } else {
        console.log('⚠️ Club document does not exist');
      }
      
      // Also check all facilities for their pitches
      try {
        const facilitiesRef = collection(db, 'clubs', clubId, 'facilities');
        const facilitiesSnapshot = await getDocs(facilitiesRef);
        console.log('🏟️ Found', facilitiesSnapshot.size, 'facilities to check');
        
        // Helper function (redeclare in this scope)
        const extractPitchNum = (pitch) => {
          if (pitch.pitchNumber) return parseInt(pitch.pitchNumber, 10) || 0;
          if (pitch.id) {
            const m = pitch.id.match(/(\d+)/);
            if (m) return parseInt(m[1], 10);
          }
          if (pitch.name) {
            const m = pitch.name.match(/(\d+)/);
            if (m) return parseInt(m[1], 10);
          }
          return 0;
        };
        
        facilitiesSnapshot.forEach(facilityDoc => {
          const facilityData = facilityDoc.data();
          console.log(`  📍 Facility: ${facilityDoc.id} (${facilityData.name || 'unnamed'})`);
          
          // Check pitches directly on facility
          if (facilityData.pitches && Array.isArray(facilityData.pitches)) {
            console.log(`    - Has ${facilityData.pitches.length} pitches at root level`);
            facilityData.pitches.forEach(p => {
              const pNum = extractPitchNum(p);
              console.log(`      - Pitch: id=${p.id}, pitchNumber=${p.pitchNumber}, name=${p.name}, extracted=${pNum}`);
              if (pNum > globalMaxPitchNumber) {
                globalMaxPitchNumber = pNum;
              }
            });
          }
          
          // Also check inside satelliteConfig
          if (facilityData.satelliteConfig?.pitches && Array.isArray(facilityData.satelliteConfig.pitches)) {
            console.log(`    - Has ${facilityData.satelliteConfig.pitches.length} pitches in satelliteConfig`);
            facilityData.satelliteConfig.pitches.forEach(p => {
              const pNum = extractPitchNum(p);
              if (pNum > globalMaxPitchNumber) {
                globalMaxPitchNumber = pNum;
              }
            });
          }
          
          // Check facility pitchNames too
          if (facilityData.pitchNames) {
            Object.keys(facilityData.pitchNames).forEach(key => {
              const match = key.match(/(\d+)/);
              if (match) {
                const num = parseInt(match[1], 10);
                if (num > globalMaxPitchNumber) {
                  globalMaxPitchNumber = num;
                }
              }
            });
          }
        });
      } catch (facilitiesError) {
        console.warn('⚠️ Could not check facilities for pitch numbers:', facilitiesError);
      }
      
      console.log('🔢 Highest existing pitch number across all facilities:', globalMaxPitchNumber);

      // In EDIT MODE: preserve existing pitch IDs/numbers, only number NEW pitches
      // In NEW MODE: number all pitches from globalMaxPitchNumber + 1
      let nextNewPitchNumber = globalMaxPitchNumber + 1;
      
      const flatPitchData = manualPitches.map((pitch, index) => {
        // Check if this pitch already has valid ID and pitchNumber (existing pitch)
        const hasExistingId = pitch.id && pitch.id.startsWith('pitch-');
        const hasExistingNumber = pitch.pitchNumber && typeof pitch.pitchNumber === 'number';
        
        if (isEditMode && hasExistingId && hasExistingNumber) {
          // PRESERVE existing pitch data
          console.log(`📝 Preserving existing pitch - id: ${pitch.id}, pitchNumber: ${pitch.pitchNumber}, name: ${pitch.name}`);
          return {
            id: pitch.id,
            name: pitch.name || `Pitch ${pitch.pitchNumber}`,
            pitchNumber: pitch.pitchNumber,
            centerX: pitch.centerX,
            centerY: pitch.centerY,
            width: pitch.width,
            height: pitch.height,
            rotation: pitch.rotation,
            corners: pitch.corners || null,
            surfaceType: pitch.surfaceType || 'grass',
            status: pitch.status || 'available',
            sections: pitch.sections || null,
            createdAt: pitch.createdAt || new Date().toISOString(),
            manuallyDrawn: true
          };
        } else {
          // NEW pitch - assign next available number
          const pitchNumber = nextNewPitchNumber++;
          const pitchName = `Pitch ${pitchNumber}`;
          console.log(`📝 Creating NEW pitch - pitchNumber: ${pitchNumber}, name: ${pitchName}`);
          return {
            id: `pitch-${pitchNumber}`,
            name: pitchName,
            pitchNumber: pitchNumber,
            centerX: pitch.centerX,
            centerY: pitch.centerY,
            width: pitch.width,
            height: pitch.height,
            rotation: pitch.rotation,
            corners: pitch.corners || null,
            surfaceType: 'grass',
            status: 'available',
            createdAt: new Date().toISOString(),
            manuallyDrawn: true
          };
        }
      });
      
      console.log('📊 Final pitch data to save:', JSON.stringify(flatPitchData.map(p => ({
        id: p.id,
        name: p.name,
        pitchNumber: p.pitchNumber,
        pitchNumberType: typeof p.pitchNumber
      }))));

      // Build satellite config
      const satelliteConfig = {
        imageUrl: imageUrl,
        imageWidth: 2048,
        imageHeight: 1536,
        pitches: flatPitchData,
        location: mapLocation || (existingConfig?.location || null),
        zoom: map ? map.getZoom() : (existingConfig?.zoom || 18),
        capturedAt: new Date().toISOString()
      };

      // SIMPLIFIED: Save to ONE place only - the facility document
      if (facilityId) {
        const facilityRef = doc(db, 'clubs', clubId, 'facilities', facilityId);
        await setDoc(facilityRef, {
          satelliteConfig: satelliteConfig,
          pitches: flatPitchData,
          satelliteImageUrl: imageUrl,
          imageWidth: 2048,
          imageHeight: 1536,
          facilityConfigured: true,
          configurationMethod: 'manual_draw',
          lastUpdated: serverTimestamp()
        }, { merge: true });
        console.log('✅ Saved to FACILITY document:', facilityId);
        console.log('📊 Pitches saved:', flatPitchData.map(p => ({ id: p.id, name: p.name })));
      } else {
        // No facilityId - save to club document (legacy mode for clubs without facilities)
        console.log('💾 Saving to club document (legacy mode - no facilityId)');
        await updateDoc(clubDocRef, {
          satelliteImageUrl: imageUrl,
          satelliteImage: imageUrl,
          satelliteImageCapturedAt: serverTimestamp(),
          pitches: flatPitchData,
          pitchesLastUpdated: serverTimestamp(),
          facilityConfigured: true,
          configurationMethod: 'manual_draw',
          imageWidth: 2048,
          imageHeight: 1536,
          satelliteConfig: satelliteConfig
        });
      }

      console.log('✅ Pitch data saved successfully');
      
      setSuccessMessage(`Successfully saved ${manualPitches.length} pitch(es)!`);
      
      // Mark save as completed - this prevents the UI from resetting
      setSaveCompleted(true);
      
      // Call the callback if provided - pass data in expected format for FacilitySetupWizard
      if (onCaptureComplete) {
        onCaptureComplete({ 
          pitches: flatPitchData, 
          imageUrl: imageUrl,
          satelliteImage: imageUrl,
          satelliteImageUrl: imageUrl,
          satelliteConfig: satelliteConfig,
          imageWidth: 2048,
          imageHeight: 1536,
          facilityId: facilityId,
          configurationMethod: 'manual_draw'
        });
      }
      
      // DO NOT reset state here!
      // The parent FacilitySetupWizard has an onSnapshot listener that will
      // detect hasImage && hasPitches and automatically show the success state

    } catch (error) {
      console.error('Error saving manual pitches:', error);
      setMapError(error.message || 'Failed to save pitch configuration');
    } finally {
      setIsSavingManual(false);
    }
  };

  // ============================================================================
  // GOOGLE MAPS INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapError('Google Maps API key is required');
      return;
    }

    const initMap = () => {
      try {
        const google = window.google;
        if (!google || !google.maps) {
          throw new Error('Google Maps failed to load');
        }

        // Default to Adelaide, Australia
        const defaultCenter = { lat: -34.9285, lng: 138.6007 };

        const mapInstance = new google.maps.Map(mapRef.current, {
          center: defaultCenter,
          zoom: 18,
          mapTypeId: 'satellite',
          tilt: 0,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          rotateControl: false
        });

        setMap(mapInstance);

        // Setup Places Autocomplete
        if (google.maps.places && searchInputRef.current) {
          const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
            types: ['establishment', 'geocode'],
            fields: ['geometry', 'name', 'formatted_address']
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
              mapInstance.setCenter(place.geometry.location);
              mapInstance.setZoom(18);
              setShowSearchHint(false);
            }
          });
        }

        setMapError(null);
      } catch (error) {
        console.error('Map initialization error:', error);
        setMapError('Failed to initialize map. Please refresh the page.');
      }
    };

    // Load Google Maps script if not already loaded
    if (window.google && window.google.maps) {
      initMap();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => setMapError('Failed to load Google Maps');
      document.head.appendChild(script);
    }
  }, [googleMapsApiKey]);

  // ============================================================================
  // CAPTURE FUNCTION
  // ============================================================================

  const captureAndDetect = async () => {
    if (!map) {
      setMapError('Map not ready. Please wait...');
      return;
    }

    setIsCapturing(true);
    setMapError(null);
    setSuccessMessage(null);

    try {
      const center = map.getCenter();
      const zoom = map.getZoom();

      // Capture high-resolution satellite image using Static Maps API
      const width = 2048;
      const height = 1536;
      
      const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat()},${center.lng()}&zoom=${zoom}&size=${width}x${height}&scale=2&maptype=satellite&key=${googleMapsApiKey}`;

      // Fetch the image
      const response = await fetch(staticMapUrl);
      if (!response.ok) {
        throw new Error('Failed to capture satellite image');
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      console.log('📸 NEW IMAGE CAPTURED');
      console.log('🗺️ Map center:', center.lat(), center.lng());
      console.log('🔍 Zoom level:', zoom);
      console.log('🖼️ Image URL:', imageUrl.substring(0, 50) + '...');

      setCapturedImageBlob(blob);
      setCapturedImageUrl(imageUrl);
      setShowManualDraw(true);
      setShowCaptureHint(false);
      
      // CRITICAL: Clear existing pitches when recapturing
      console.log('🧹 Clearing all existing pitches');
      setManualPitches([]);
      setPendingPitch(null);
      setCurrentPoints([]);

    } catch (error) {
      console.error('Capture error:', error);
      setMapError(error.message || 'Failed to capture image');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleBackToMap = () => {
    setShowManualDraw(false);
    setManualPitches([]);
    setPendingPitch(null);
    setCurrentPoints([]);
    setIsDrawing(false);
    setCapturedImageBlob(null);
    setCapturedImageUrl(null);
  };

  // ============================================================================
  // RENDER - MANUAL DRAWING INTERFACE
  // ============================================================================

  if (showManualDraw && capturedImageUrl) {
    return (
      <>
        {isSavingManual && <LoadingTransition message="Saving your facility configuration..." />}
        
        {/* OVERLAP WARNING MODAL */}
        {overlapWarning && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10010
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Warning Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertCircle size={28} color="#f59e0b" />
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1f2937'
                  }}>
                    Overlapping Pitch Detected
                  </h2>
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    This pitch overlaps with {overlapWarning.overlappingNames.join(', ')}
                  </p>
                </div>
              </div>

              {/* Warning Message */}
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#92400e',
                  lineHeight: '1.6'
                }}>
                  <strong>Why this matters:</strong> Overlapping pitches can cause scheduling conflicts that won't be detected by the system. 
                  If you need to allocate different sized areas on the same physical space, use <strong>sections</strong> within a single pitch instead.
                </p>
              </div>

              {/* Examples Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '16px'
                }}>
                  How to use sections correctly:
                </h3>

                {/* Bad Example */}
                <div style={{
                  border: '2px solid #fee2e2',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: '#fef2f2'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ✗ AVOID
                    </span>
                    <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '500' }}>
                      Drawing overlapping pitches
                    </span>
                  </div>
                  <p style={{
                    fontSize: '13px',
                    color: '#7f1d1d',
                    margin: '0 0 12px 0',
                    lineHeight: '1.5'
                  }}>
                    Multiple pitches covering the same area will cause undetected conflicts when scheduling.
                  </p>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '8px',
                    textAlign: 'center'
                  }}>
                    <img 
                      src="/overlap-warning-bad-example.jpg" 
                      alt="Example of overlapping pitches - avoid this"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '150px',
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="padding: 20px; color: #9ca3af; font-size: 13px;">Image: Overlapping pitches example</div>';
                      }}
                    />
                  </div>
                </div>

                {/* Good Examples */}
                <div style={{
                  border: '2px solid #d1fae5',
                  borderRadius: '12px',
                  padding: '16px',
                  backgroundColor: '#ecfdf5'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span style={{
                      backgroundColor: '#16a34a',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ✓ RECOMMENDED
                    </span>
                    <span style={{ fontSize: '14px', color: '#166534', fontWeight: '500' }}>
                      Use sections within one pitch
                    </span>
                  </div>
                  <p style={{
                    fontSize: '13px',
                    color: '#166534',
                    margin: '0 0 12px 0',
                    lineHeight: '1.5'
                  }}>
                    Draw one pitch and allocate sections based on team requirements. The system will detect conflicts automatically.
                  </p>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '12px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#6b7280', 
                        margin: '0 0 10px 0',
                        fontWeight: '500'
                      }}>
                        For a half match pitch configuration assign 4 sections each
                      </p>
                      <img 
                        src="/overlap-warning-sections-half.jpg" 
                        alt="Half pitch allocation example"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML += '<div style="padding: 10px; color: #9ca3af; font-size: 12px;">Half pitch example</div>';
                        }}
                      />
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '12px',
                      textAlign: 'center'
                    }}>
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#6b7280', 
                        margin: '0 0 10px 0',
                        fontWeight: '500'
                      }}>
                        For a full pitch configuration assign 8 sections
                      </p>
                      <img 
                        src="/overlap-warning-sections-full.jpg" 
                        alt="Full pitch allocation example"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML += '<div style="padding: 10px; color: #9ca3af; font-size: 12px;">Full pitch example</div>';
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleCancelOverlappingPitch}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                >
                  Cancel & Adjust
                </button>
                <button
                  onClick={handleConfirmOverlappingPitch}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  Add Anyway
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Show success state if save completed - ONLY if no callback (standalone mode) */}
        {/* When onCaptureComplete is provided, parent component handles the next step */}
        {saveCompleted && !onCaptureComplete && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10006
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>✅</div>
            <h2 style={{ 
              color: '#15803d', 
              fontSize: '24px', 
              fontWeight: '700',
              marginBottom: '12px'
            }}>
              Pitches Saved Successfully!
            </h2>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>
              Loading your facility setup...
            </p>
            <div style={{ marginTop: '24px' }}>
              <Loader size={32} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          </div>
        )}
        
        {/* Wrapper for white box + floating instructions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '24px',
          padding: '0 24px'
        }}>
          {/* Main white container */}
          <div style={{ backgroundColor: 'white', flex: '1', maxWidth: '1400px' }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Draw Your Pitch Boundaries
              </h3>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Click 4 corners to define each pitch area
              </p>
            </div>
            <button
              onClick={handleBackToMap}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ← Back to Map
            </button>
          </div>

          {/* Info message about numbering */}
          <div style={{
            padding: '12px 24px',
            backgroundColor: '#eff6ff',
            borderBottom: '1px solid #bfdbfe',
            fontSize: '13px',
            color: '#1e40af',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontWeight: '600' }}>Note:</span>
            Pitches will be numbered 1, 2, 3... in the order you draw them. 
            This replaces any existing pitches on this facility.
          </div>

          {/* Canvas Container */}
          <div style={{
            padding: '24px',
            backgroundColor: '#f9fafb',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {/* Canvas Area */}
            <div style={{ position: 'relative' }}>
              <canvas
                ref={canvasRef}
                width={2048}
                height={1536}
                onClick={handleCanvasClick}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '780px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: draggingCorner ? 'grabbing' : (isDrawing ? 'crosshair' : (selectedPitchIndex !== null ? 'grab' : 'pointer'))
                }}
              />
              
              {/* Drawing status overlay */}
              {isDrawing && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  Click corner {currentPoints.length + 1} of 4
                </div>
              )}

              {/* CORNER EDITING: Selection instruction overlay */}
              {!isDrawing && !pendingPitch && selectedPitchIndex !== null && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  {manualPitches[selectedPitchIndex]?.name} selected - drag corners to adjust
                </div>
              )}
            </div>
          </div>

          {/* Pitch List and Controls */}
          <div style={{
            padding: '20px 24px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: 'white'
          }}>
            {/* Pending pitch rotation control */}
            {pendingPitch && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#eff6ff',
                borderRadius: '12px',
                border: '2px solid #3b82f6'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontWeight: '600', color: '#1e40af' }}>
                    Adjust New Pitch
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleConfirmPitch}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      ✓ Confirm
                    </button>
                    <button
                      onClick={handleCancelPitch}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '14px', color: '#374151' }}>Rotation:</span>
                  <input
                    type="range"
                    min="-45"
                    max="45"
                    value={rotationAdjustment}
                    onChange={(e) => setRotationAdjustment(Number(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '50px' }}>
                    {rotationAdjustment}°
                  </span>
                </div>
              </div>
            )}

            {/* Existing pitches list */}
            {manualPitches.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '16px', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Drawn Pitches ({manualPitches.length})
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {manualPitches.map((pitch, index) => {
                    const isSelected = selectedPitchIndex === index;
                    return (
                      <div
                        key={index}
                        onClick={() => setSelectedPitchIndex(isSelected ? null : index)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: isSelected ? '#fef2f2' : '#dcfce7',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid #dc2626' : '1px solid #86efac',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ 
                          color: isSelected ? '#dc2626' : '#166534', 
                          fontWeight: isSelected ? '600' : '500' 
                        }}>
                          {pitch.name}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePitch(index);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '2px',
                            fontSize: '16px',
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div>
                {!isDrawing && !pendingPitch && (
                  <button
                    onClick={handleStartDrawing}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Edit3 size={18} />
                    Draw New Pitch
                  </button>
                )}
                
                {isDrawing && (
                  <button
                    onClick={handleCancelDrawing}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Drawing
                  </button>
                )}
              </div>

              {manualPitches.length > 0 && !isDrawing && !pendingPitch && (
                <button
                  onClick={handleSaveManualPitches}
                  disabled={isSavingManual}
                  style={{
                    padding: '14px 32px',
                    backgroundColor: isSavingManual ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: isSavingManual ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSavingManual ? (
                    <>
                      <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Save {manualPitches.length} Pitch{manualPitches.length > 1 ? 'es' : ''}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Error/Success messages */}
          {(mapError || successMessage) && (
            <div style={{ padding: '0 24px 20px' }}>
              {mapError && (
                <div style={{
                  padding: '14px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <AlertCircle size={20} color="#dc2626" style={{ marginRight: '12px' }} />
                  <span style={{ color: '#dc2626', fontSize: '14px' }}>{mapError}</span>
                </div>
              )}
              {successMessage && (
                <div style={{
                  padding: '14px',
                  backgroundColor: '#dcfce7',
                  border: '1px solid #86efac',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Check size={20} color="#16a34a" style={{ marginRight: '12px' }} />
                  <span style={{ color: '#16a34a', fontSize: '14px' }}>{successMessage}</span>
                </div>
              )}
            </div>
          )}
        </div>

          {/* Instructions Panel - Outside white box in right margin */}
          <div style={{
            width: '200px',
            flexShrink: 0,
            backgroundColor: '#eff6ff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #bfdbfe',
            alignSelf: 'flex-start',
            marginTop: '80px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#1e40af',
              fontWeight: '600',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📌 Instructions
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>1</span>
                <span style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.4' }}>
                  Click "Draw New Pitch"
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>2</span>
                <span style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.4' }}>
                  Click 4 corners clockwise
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>3</span>
                <span style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.4' }}>
                  Adjust rotation if needed
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  flexShrink: 0
                }}>4</span>
                <span style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.4' }}>
                  Confirm
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // ============================================================================
  // RENDER - MAP CAPTURE INTERFACE
  // ============================================================================

  return (
    <>
      {isCapturing && <LoadingTransition message="Capturing satellite image..." />}
      
      <div style={{ backgroundColor: 'white' }}>
      <div style={{ width: '100%' }}>
        {/* Header with Instructions */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '20px',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            📍 Capture Your Facility
          </h3>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            Search for your club location and capture a satellite image
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                1
              </div>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Find your club by entering the name into the search bar
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                2
              </div>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Centre the satellite view of your club, be sure not to zoom in too close
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                3
              </div>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#6b7280',
                lineHeight: '1.6'
              }}>
                Click Capture Image
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{
          padding: '20px 24px',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            position: 'relative',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <Search 
              size={20} 
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for your club location..."
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                outline: 'none',
                backgroundColor: 'white',
                transition: 'border-color 0.2s',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            {showSearchHint && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '20px',
                marginTop: '12px',
                zIndex: 10
              }}>
                <Hint 
                  text="Enter the name of your facility and press enter" 
                  position="bottom"
                  onDismiss={handleDismissSearchHint}
                />
              </div>
            )}
          </div>
        </div>

        {/* Map View */}
        <div 
          ref={mapRef}
          style={{
            width: '100%',
            height: '600px',
            backgroundColor: '#f3f4f6'
          }}
        />

        {/* Bottom Action Section */}
        <div style={{
          padding: '24px',
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb'
        }}>
          {mapError && (
            <div style={{
              marginBottom: '16px',
              padding: '14px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <AlertCircle size={20} color="#dc2626" style={{ marginRight: '12px', flexShrink: 0 }} />
              <span style={{ color: '#dc2626', fontSize: '14px' }}>{mapError}</span>
            </div>
          )}

          {successMessage && (
            <div style={{
              marginBottom: '16px',
              padding: '14px',
              backgroundColor: '#dcfce7',
              border: '1px solid #86efac',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Check size={20} color="#16a34a" style={{ marginRight: '12px' }} />
              <span style={{ color: '#16a34a', fontSize: '14px' }}>{successMessage}</span>
            </div>
          )}

          <div style={{ 
            position: 'relative',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <button
              onClick={captureAndDetect}
              disabled={isCapturing}
              style={{
                width: '100%',
                padding: '16px 24px',
                backgroundColor: isCapturing ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isCapturing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.2s',
                boxShadow: isCapturing ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.3)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
              onMouseEnter={(e) => {
                if (!isCapturing) {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCapturing) {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                }
              }}
            >
              {isCapturing ? (
                <>
                  <Loader size={20} className="spinning" />
                  Capturing Image...
                </>
              ) : (
                <>
                  <Camera size={20} />
                  Capture Image
                </>
              )}
            </button>
            
            {showCaptureHint && !isCapturing && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '12px',
                zIndex: 10
              }}>
                <Hint 
                  text="Click here to capture the map and start drawing pitches" 
                  position="top"
                  onDismiss={handleDismissCaptureHint}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default GoogleMapsSatelliteUpload;
